import { Button, Dialog, DialogActions, Stack, Typography } from "@mui/material";
import { GlobalState } from "context/GlobalContext";
import { arrType, datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import { useEffect, useMemo, useState } from "react";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import FileSaver from "file-saver";
import axios from "axios";
import moment from "moment";

export const trimsExportLevelOptions = {
	MASTER: { key: "MASTER", name: "Trim Master Summary" },
	SUPPLIER: { key: "SUPPLIER", name: "Supplier & Pricing" },
	SAMPLE: { key: "SAMPLE", name: "Sample Tracking" },
};

const exportLevelColumn = {
	name: "Export Level",
	key: "export_level",
	input_type: inputTypes.SELECT.key,
	data_type: datatypes.STRING.key,
	constraints: { NOTNULL: true, DISPLAY: true },
	[selectOptionsKeyName]: {
		type: arrType,
		list: Object.values(trimsExportLevelOptions),
	},
};

const exportFileNameByLevel = {
	[trimsExportLevelOptions.MASTER.key]: "TrimsMaster_Summary",
	[trimsExportLevelOptions.SUPPLIER.key]: "TrimsMaster_Supplier",
	[trimsExportLevelOptions.SAMPLE.key]: "TrimsMaster_Sample",
};

const DataExportModal = ({ modal, handleCloseModal, filterColumnVal, blockSupplierDetails = false }) => {
	const { snackBar, setSnackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const availableExportLevels = useMemo(
		() =>
			blockSupplierDetails
				? { [trimsExportLevelOptions.MASTER.key]: trimsExportLevelOptions.MASTER }
				: trimsExportLevelOptions,
		[blockSupplierDetails],
	);

	const [exportState, setExportState] = useState({
		[exportLevelColumn.key]: trimsExportLevelOptions.MASTER.key,
	});

	useEffect(() => {
		if (!blockSupplierDetails) return;
		setExportState((prev) => ({
			...prev,
			[exportLevelColumn.key]: trimsExportLevelOptions.MASTER.key,
		}));
	}, [blockSupplierDetails]);

	const handleDownloadClick = async () => {
		const exportLevel = exportState[exportLevelColumn.key];
		try {
			setLoadingOpen(true);
			const res = await axios({
				url: `/api/sourcing/trims_master/download_trims_master`,
				method: "POST",
				responseType: "blob",
				data: {
					filter: filterColumnVal,
					export_level: exportLevel,
				},
			});

			const url = URL.createObjectURL(res.data);
			const fileName = exportFileNameByLevel[exportLevel] || "TrimsMaster";
			FileSaver.saveAs(url, `${fileName}_${moment().format("DD-MMM-yy_HH-mm")}.xlsx`);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "File Downloaded Successfully",
				severity: "success",
			});
			handleCloseModal();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Dialog fullWidth maxWidth="sm" open={modal} onClose={handleCloseModal} scroll="body">
			<Stack width="100%" p={2} boxSizing="border-box" spacing={1.5}>
				<Typography variant="h6">Export Trims Master</Typography>
				<Typography variant="body2" color="text.secondary">
					Choose how detailed the export should be. Filters from the grid will still apply.
				</Typography>
				<RenderInputField
					column={{
						...exportLevelColumn,
						[selectOptionsKeyName]: {
							type: arrType,
							list: Object.values(availableExportLevels),
						},
					}}
					data={exportState}
					setData={setExportState}
					selectOptObjName={selectOptionsKeyName}
					width="100%"
				/>
			</Stack>
			<DialogActions>
				<Button variant="outlined" onClick={handleCloseModal}>
					Cancel
				</Button>
				<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={handleDownloadClick}>
					Download
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DataExportModal;
